import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import _ from 'lodash';

import './SearchResults.scss';
import { RootState } from '../../configs/store';
import SportsBettingContext from '../SportsBetting/Context/SportsBetting.context';
import GoUpButton from '../GoUpButton/GoUpButton';
import NoResults from '../SportsBetting/NoResults/NoResults';
import Icon from '../Icon/Icon';
import useIsMobile from 'src/hooks/useIsMobile';
import SportSearch from './Components/SportSearch';
import RacingSearch from './Components/RacingSearch';

interface SearchResultsProps {
  /**
   * External closeSearch function
   */
  closeSearch?: () => void,
  /**
   * If search results is showing top filter events
   */
  isTopFilters?: boolean,
  children?: React.ReactNode
}

export default function SearchResults({ closeSearch, children }: SearchResultsProps) {
  // STATE
  const [maxMainMarkets, setMaxMainMarkets] = useState(1);
  const [maxFilterButtons, setMaxFilterButtons] = useState(2);
  const [keyword, setKeyword] = useState('');

  const { t } = useTranslation('common');
  const mobile = useIsMobile();
  const containerRef = useRef(null);
  const context = {
    maxMainMarkets,
    maxFilterButtons
  };

  // REDUX
  const {
    isSearching,
    search,
    searchResults,
    loading
  } = useSelector((state: RootState) => state.events);
  const dispatch = useDispatch();

  const onCloseClick = () => {
    dispatch.events.getSportSearchResults('');
  };

  const currentEvents = searchResults.list;

  useLayoutEffect(() => {
    let resizeObs: ResizeObserver;
    const containerRefCurrent = containerRef.current;

    const componentResized = () => {
      if (containerRefCurrent) {
        const containerWidth = containerRefCurrent.getBoundingClientRect().width;

        if (containerWidth > 0 && containerWidth <= 550 && maxMainMarkets > 1) {
          setMaxMainMarkets(1);
        } else {

          const max = Math.floor((containerWidth - (!isMobile ? 500 : 300)) / 144);
          if (max !== maxMainMarkets && max > 0) {
            setMaxMainMarkets(max);
          }
        }

        const maxFilterWidth = Math.floor(containerWidth / 230);
        if (maxFilterWidth !== maxFilterButtons && maxFilterWidth > 0) {
          setMaxFilterButtons(maxFilterWidth);
        }
      }
    };

    if (containerRefCurrent) {
      resizeObs = new ResizeObserver(componentResized);
      resizeObs.observe(containerRefCurrent);
    }

    window.addEventListener('resize', componentResized);
    componentResized();

    return () => {
      window.removeEventListener('resize', componentResized);
      if (containerRefCurrent) {
        resizeObs.unobserve(containerRefCurrent);
      }
    };
  }, [maxMainMarkets, maxFilterButtons]);

  useEffect(() => {
    setKeyword(search);
  }, [currentEvents]);

  return (
    <SportsBettingContext.Provider value={ context }>
      <div className="search-results" ref={ containerRef }>
        {
          isSearching && !mobile && (
            <div className="sports-betting__title-container">
              <Icon icon="searchIcon" className="search-results__search-icon" />
              <span className="sports-betting__title">
                { searchResults.totals } { t('sports.results') }
                <span className="search-results__search-data">&quot;{ keyword }&quot;</span>
                <Icon
                  onClick={ closeSearch ? closeSearch : onCloseClick }
                  icon="close"
                  className="search-results__close"
                />
              </span>
            </div>
          )
        }
        {
          isSearching && mobile && (
            <>
              <div className="bottom-navigation__search-result">
                <Icon icon="searchIcon" className="bottom-navigation__search-icon" />
                <span className="bottom-navigation__results">
                  { searchResults.totals } { t('sports.results') }
                </span>
              </div>
              <span className="bottom-navigation__search-text">
                &quot;{ keyword }&quot;
              </span>
            </>
          )
        }

        {
          _.isEmpty(currentEvents) && !loading && isSearching && <NoResults closeSearch={ closeSearch ? closeSearch : onCloseClick } />
        }
        {
          children
        }
        <GoUpButton />
      </div>
    </SportsBettingContext.Provider>
  );
}

SearchResults.Sports = SportSearch;
SearchResults.Racing = RacingSearch;
